<template>
    <v-app class="background" style="height: 100vh; overflow: hidden">
        <v-app-bar app v-if="showAppBar" color="green" height="65">
            <!--
            Snow overlay (winter theme) — kept for later use.
            <div class="snow-container">
                <span
                    v-for="flake in snowflakes"
                    :key="flake.id"
                    class="snowflake"
                    :style="{
                        left: flake.left + '%',
                        animationDuration: flake.duration + 's',
                        animationDelay: flake.delay + 's',
                        fontSize: flake.size + 'px',
                    }"
                >
                    ❄
                </span>
            </div>
            -->

            <!-- Flower "garden" overlay (spring theme) -->
            <div
                v-if="isAppBarAnimationEnabled"
                class="garden-container"
                aria-hidden="true"
            >
                <div
                    v-for="flower in flowers"
                    :key="flower.id"
                    class="flower"
                    :style="{
                        left: flower.left + '%',
                        '--stem-h': flower.stemH + 'px',
                        '--grow-dur': flower.duration + 's',
                        '--grow-delay': flower.delay + 's',
                        '--bloom': flower.bloomColor,
                    }"
                >
                    <div class="stem"></div>
                    <div class="bloom"></div>
                </div>
            </div>

            <v-toolbar-title
                >Hey, {{ user?.name || user?.email }}</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-switch
                v-if="isOnOperatorDashboard && user?.overtimeToggleEnabled"
                :model-value="overtimeVisualOn"
                :disabled="isLunch"
                color="primary"
                hide-details
                class="ml-4 mr-6"
                @update:modelValue="forwardOvertimeToggle"
            >
                <template v-slot:label>
                    <span
                        @click.prevent.stop
                        @mousedown.prevent.stop
                        @touchstart.prevent.stop
                    >
                        {{
                            overtimeVisualOn
                                ? "Stop Overtime"
                                : "Start Overtime"
                        }}
                    </span>
                </template>
            </v-switch>
            <!-- Dropdown Menu -->
            <v-menu offset-y>
                <template v-slot:activator="{ props }">
                    <v-btn icon v-bind="props">
                        <v-icon>mdi-menu</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item @click="goTo('UserDashboard')">
                        <v-list-item-title>Dashboard</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="canViewMenuTimeManagementItem"
                        @click="goTo('OperatorDashboard')"
                    >
                        <v-list-item-title>Time Management</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="canViewMenuTeamLeadersItem"
                        @click="goTo('UserDashboard', { tab: 'teamleaders' })"
                    >
                        <v-list-item-title>TeamLeaders</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="canViewMenuUsersItem"
                        @click="goTo('UserDashboard', { tab: 'users' })"
                    >
                        <v-list-item-title>Users</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="canViewMenuProjectsItem"
                        @click="goTo('UserDashboard', { tab: 'projects' })"
                    >
                        <v-list-item-title>Projects</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="goTo('DailyReport')">
                        <v-list-item-title>Daily Report</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-if="canViewMenuReportsItem"
                        @click="goTo('Statistics')"
                    >
                        <v-list-item-title>Statistics</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                        class="bg-error"
                        v-if="canRestartApp"
                        @click="sendRestartToAllUsers"
                    >
                        <v-list-item-title>Restart App</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="goTo('UserSettings')">
                        <v-list-item-title>Settings</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="logout">
                        <v-list-item-title>Log off</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-app-bar>

        <v-main
            class="d-flex flex-column"
            style="min-height: 0; overflow: hidden; height: 100%"
        >
            <MessageSnackbar
                v-for="(snackbar, index) in snackbars"
                :key="index"
                v-model:show="snackbar.show"
                :color="snackbar.color"
                :timeout="snackbar.timeout || 600000"
                location="top"
                :style="{ top: `${index === 0 ? 0 : index * 150}px` }"
                :closeButton="true"
                :rounded="false"
            >
                <template #default>
                    <div class="d-flex flex-column ga-4" style="width: 360px">
                        <div class="d-flex justify-start flex-row ga-4">
                            <div
                                class="d-flex justify-center align-center"
                                style="
                                    width: 48px;
                                    height: 48px;
                                    border: 7px;
                                    border-radius: 50px;
                                    padding: 10px;
                                    background-color: #ffffff29;
                                "
                            >
                                <v-icon>{{
                                    getPriorityIcon(snackbar.project.priority)
                                }}</v-icon>
                            </div>
                            <div>
                                <strong>{{ snackbar.project.name }}</strong
                                >'s priority has has been changed to
                                <strong>{{ snackbar.project.priority }}.</strong
                                ><br />
                                <span
                                    >It requires your immediate attention.</span
                                >
                            </div>
                        </div>
                        <div class="d-flex justify-space-between flex-row">
                            <v-btn
                                variant="text"
                                @click="
                                    redirectAndStartProject(snackbar.project)
                                "
                            >
                                Start Project
                            </v-btn>
                            <v-btn variant="text" @click="removeSnackbar(index)"
                                >OK</v-btn
                            >
                        </div>
                    </div>
                </template>
            </MessageSnackbar>

            <router-view v-slot="{ Component, route }" ref="routedView">
                <v-slide-y-reverse-transition
                    v-if="showAppBar"
                    mode="out-in"
                >
                    <component :is="Component" :key="route.name" />
                </v-slide-y-reverse-transition>
                <component
                    v-else
                    :is="Component"
                    :key="route.name"
                />
            </router-view>
        </v-main>
    </v-app>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
    logoutUser,
    sendApplicationRestartSignal,
} from "@/services/firebaseService";
import MessageSnackbar from "@/components/MessageSnackbar.vue";
import { getActiveEntry } from "@/utils/utils";

export default {
    name: "App",
    components: { MessageSnackbar },
    data() {
        return {
            snackbars: [],
            previousPriorities: {}, // Tracks the previous priority state
            priorityColors: {
                Highest: "deep-purple darken-1",
                High: "error",
                Normal: "success",
                Low: "primary",
                Lowest: "text-primary",
            },
            overtimeEnabled: false,

            // Flower config (spring theme)
            flowers: Array.from({ length: 50 }, (_, i) => ({
                id: i,
                left: Math.random() * 100, // 0–100% across the bar
                stemH: 10 + Math.round(Math.random() * 16), // 10–26px stem
                duration: 4 + Math.random() * 5, // 4–9s cycle
                delay: Math.random() * 4, // staggered
                bloomColor: [
                    "#FFEB3B", // yellow
                    "#FFCDD2", // soft pink
                    "#E1BEE7", // lavender
                    "#BBDEFB", // light blue
                    "#C8E6C9", // mint
                    "#FFE0B2", // peach
                ][Math.floor(Math.random() * 6)],
            })),

            /*
            Snowflake config (winter theme) — kept for later use.
            snowflakes: Array.from({ length: 100 }, (_, i) => ({
                id: i,
                left: Math.random() * 100, // 0–100% across the bar
                duration: 4 + Math.random() * 6, // 4–10s fall time
                delay: Math.random() * 10, // staggered starts
                size: 10 + Math.random() * 8, // 10–18px font size
            })),
            */
        };
    },
    computed: {
        ...mapGetters({
            user: "getUser",
            rights: "getRights",
            projects: "getWorkingProjects",
            activeProject: "getActiveProject",
        }),
        showAppBar() {
            // Show app bar only on authenticated routes
            return (
                this.$route?.matched?.some(
                    (record) => record.meta?.requiresAuth
                ) || false
            );
        },
        isOnOperatorDashboard() {
            // Always show toggle on OperatorDashboard route (Time Management for admins/TLs)
            if (this.$route.name === "OperatorDashboard") {
                return true;
            }
            // Show toggle on UserDashboard route ONLY if user is an operator (their landing page)
            if (this.$route.name === "UserDashboard") {
                return this.rights?.viewOperatorDashboard === true;
            }
            return false;
        },
        activeEntry() {
            return getActiveEntry(this.user);
        },
        currentProjectFromEntry() {
            const activeEntry = this.activeEntry;
            if (!activeEntry) return null;
            return (
                (this.projects || []).find(
                    (p) => p.projectId === activeEntry.projectId
                ) || null
            );
        },
        isLunch() {
            return (
                this.activeProject?.projectId === "SYSTEM_LUNCH-BREAK" ||
                this.currentProjectFromEntry?.projectId === "SYSTEM_LUNCH-BREAK"
            );
        },
        overtimeVisualOn() {
            if (this.isLunch) return false;
            const entry = this.activeEntry;
            if (entry) return !!entry.overtime;
            return this.user?.userStatus === "inOvertime";
        },
        // Get user.projects as an object
        userProjects() {
            return this.user?.projects || {};
        },
        canViewMenuReportsItem() {
            return this.rights?.viewMenuReportsItem || false;
        },
        canViewMenuProjectsItem() {
            return this.rights?.viewMenuProjectsItem || false;
        },
        canViewMenuTeamLeadersItem() {
            return this.rights?.viewMenuTeamLeadersItem || false;
        },
        canViewMenuTimeManagementItem() {
            return this.rights?.viewMenuTimeManagementItem || false;
        },
        canViewMenuUsersItem() {
            return this.rights?.viewMenuUsersItem || false;
        },
        canRestartApp() {
            return this.rights?.canRestartApp || false;
        },
        isAppBarAnimationEnabled() {
            // Default is OFF when missing.
            return this.user?.appBarAnimation === true;
        },
    },
    watch: {
        projects: {
            deep: true,
            immediate: true, // Initialize previousPriorities on load
            handler(newProjects) {
                if (!newProjects) return; // Ensure the newProjects array exists
                if (this.$store.state.priorityResetInProgress) return;
                // Detect changes in priority
                newProjects.forEach((project) => {
                    const oldPriority =
                        this.previousPriorities[project.projectId];
                    const newPriority = project.priority;

                    // If the priority has changed, handle notifications
                    if (oldPriority && oldPriority !== newPriority) {
                        // Always remove existing snackbars for the same project when priority changes
                        this.snackbars = this.snackbars.filter(
                            (snackbar) =>
                                snackbar.project.projectId !== project.projectId
                        );

                        // Only add a new snackbar if the new priority is High or Highest
                        if (
                            newPriority === "High" ||
                            newPriority === "Highest"
                        ) {
                            this.snackbars.unshift({
                                color:
                                    this.priorityColors[newPriority] ||
                                    "primary",
                                show: true,
                                project: project,
                            });

                            // Send a system notification
                            this.sendSystemNotification(project, newPriority);
                        }
                    }
                    // Update the tracked priority
                    this.previousPriorities[project.projectId] = newPriority;
                });
            },
        },

        // Watch for changes in user.projects
        userProjects: {
            deep: true,
            immediate: true,
            handler(newUserProjects) {
                if (this.$store.state.priorityResetInProgress) return;

                const previousProjects =
                    this.$store.state.previousUserProjectPriorities;

                // Loop through the keys of userProjects (project IDs)
                Object.keys(newUserProjects).forEach((projectId) => {
                    const project = newUserProjects[projectId];
                    const oldPriority = previousProjects[projectId]?.priority;
                    const newPriority = project.priority;

                    // If the priority has changed, handle snackbar notifications
                    // Only show notifications when priority changes TO High or Highest
                    // Always remove existing snackbars when priority changes (even if decreasing)
                    if (oldPriority && oldPriority !== newPriority) {
                        // Always remove any existing snackbar for the project when priority changes
                        this.snackbars = this.snackbars.filter(
                            (snackbar) =>
                                snackbar.project.projectId !== project.projectId
                        );

                        // Only add a new snackbar if the new priority is High or Highest
                        if (
                            newPriority === "High" ||
                            newPriority === "Highest"
                        ) {
                            this.snackbars.unshift({
                                color:
                                    this.priorityColors[newPriority] ||
                                    "primary",
                                show: true,
                                project: project,
                            });
                            this.sendSystemNotification(project, newPriority);
                        }
                        
                        // Always update snapshot after priority change (whether notification shown or not)
                        this.$store.state.previousUserProjectPriorities[
                            projectId
                        ] = {
                            ...project,
                        };
                    }
                });
            },
        },
        user: {
            handler(u, prev) {
                const prevStatus = (prev?.accountStatus || "").toLowerCase();
                const curStatus = (u?.accountStatus || "").toLowerCase();
                if (prevStatus !== "inactive" && curStatus === "inactive") {
                    this.logout();
                }
            },
            immediate: false,
        },
    },
    created() {
        if (this.projects) {
            this.projects.forEach((project) => {
                this.previousPriorities[project.projectId] = project.priority;
            });
        }

        // Initialize Vuex-based snapshot if missing
        if (!this.$store.state.previousUserProjectPriorities) {
            this.$store.state.previousUserProjectPriorities = {};
        }

        // Sync current userProjects into the Vuex snapshot
        if (this.userProjects) {
            Object.keys(this.userProjects).forEach((projectId) => {
                this.$store.state.previousUserProjectPriorities[projectId] = {
                    ...this.userProjects[projectId],
                };
            });
        }
    },
    mounted() {
        this.requestNotificationPermission();
    },
    methods: {
        async sendRestartToAllUsers() {
            const confirmSend = confirm("Restart the app for all users?");
            if (!confirmSend) return;
            try {
                await sendApplicationRestartSignal();
            } catch (error) {
                console.error("Restart failed:", error);
                alert(
                    "Failed to send restart signal. Check console for details."
                );
            }
        },

        /**
         * Forwards overtime toggle to the routed component via Vuex.
         * Components watch the store state and react accordingly.
         */
        forwardOvertimeToggle(val) {
            // Verify we're on a supported route
            if (!this.isOnOperatorDashboard) {
                return;
            }

            // Commit to Vuex - components will watch and react
            this.$store.commit("setOvertimeToggleValue", val);
        },
        getPriorityIcon(priority) {
            const priorityIcons = {
                Highest: "mdi-alert-octagon-outline",
                High: "mdi-alert-outline", // Warning alert
                Normal: "mdi-information-outline", // Informational alert
                Low: "mdi-check-circle-outline", // Success alert
                Lowest: "mdi-check", // Minimal alert
            };
            return priorityIcons[priority] || "mdi-help-circle"; // Fallback icon
        },

        ...mapActions([
            "signOutFromStore",
            "endCurrentProject",
            "stopAllListeners",
            "addRemoveActiveUser",
            "updateUserStatus",
        ]),

        /**
         * Sends a system notification when the priority of a project changes.
         *
         * @param {Object} project - The project object for which the notification is sent.
         * @param {string} newPriority - The new priority level of the project (e.g., "High", "Highest").
         * @description
         * - Displays a system notification with the project name and its updated priority.
         * - Keeps the notification visible until the user interacts with it (`requireInteraction: true`).
         * - Automatically closes the notification after 10 minutes if no interaction occurs.
         */
        sendSystemNotification(project, newPriority) {
            // Check if the Notifications API is supported and permission is granted
            if (
                "Notification" in window &&
                Notification.permission === "granted"
            ) {
                const notification = new Notification(
                    project.name + " Priority Changed",
                    {
                        body: `${project.name}'s priority has been changed to "${newPriority}".`,
                        requireInteraction: true, // Ensures it stays until manually closed
                    }
                );

                // Close the notification after 10 minutes (600,000 milliseconds)
                setTimeout(() => {
                    notification.close();
                }, 600000);
            }
        },

        /**
         * Requests notification permission from the user.
         *
         * @description
         * - Prompts the user to grant notification permission if the Notifications API is supported.
         * - If permission is granted, the application can display system notifications.
         * - Handles the response to ensure granted permissions are acknowledged.
         */
        requestNotificationPermission() {
            if ("Notification" in window) {
                Notification.requestPermission().then((permission) => {
                    if (permission === "granted") {
                        return; // Permission granted; no further action needed
                    }
                });
            }
        },
        /**
         * Redirects the user to the OperatorDashboard route and clears all snackbars.
         *
         * @param {Object} project - The project object to start, containing its ID and name.
         * @description
         * - Navigates the user to the "OperatorDashboard" route with the project's ID and name passed as query parameters.
         * - Clears all existing snackbars by resetting the `snackbars` array.
         */
        redirectAndStartProject(project) {
            this.$router.push({
                name: "OperatorDashboard",
                query: {
                    projectId: project.projectId,
                    projectName: project.name,
                },
            });

            // Clear all snackbars
            this.snackbars = [];
        },
        goTo(routeName, query = {}) {
            this.$router.push({ name: routeName, query }).catch((err) => {
                console.error(`Navigation error: ${err.message}`);
            });
        },
        removeSnackbar(index) {
            // Remove the snackbar at the specified index
            this.snackbars.splice(index, 1);
            this.snackbars.show = false;
        },
        async logout() {
            try {
                // 1) Stop listeners BEFORE anything else async
                await this.$store.dispatch("stopAllListeners");

                const userId = this.user.uid;
                // 2) End current project
                const previousActiveProject = this.activeProject
                    ? { projectId: this.activeProject.projectId }
                    : null;
                await this.endCurrentProject();
                // 3) Remove active user/project
                if (previousActiveProject) {
                    await this.$store.dispatch("addRemoveActiveUser", {
                        oldProject: previousActiveProject,
                        newProject: null,
                        userId,
                        name: null,
                        surname: null,
                    });
                }

                await this.$store.dispatch("resetUserProjectsPriorities");
                // 4) Clear Firebase session
                await logoutUser();

                // 5) Clear Vuex state
                this.signOutFromStore();

                setTimeout(() => {
                    this.$router.push({ name: "SignIn" });
                }, 300); // small delay to allow cleanup
            } catch (err) {
                console.error("[Logout] Failed:", err);
            }
        },
    },
};
</script>
<style>
th {
    background: #f5f5f5 !important;
}

.pulse {
    animation: pulse 1s infinite;
    display: inline-block;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0;
    }
}

.custom-text-color-error {
    color: #ff5252 !important;
}

.custom-text-color-success {
    color: #4caf50 !important;
}

.custom-text-color-primary {
    color: #1976d2 !important;
}

.hidden-scrollbar {
    ::-webkit-scrollbar {
        display: none;
    }
}

.custom-button-width .v-btn__content,
.custom-button-width .v-btn__content > * {
    width: 100% !important;
}

.v-btn.custom-button-width {
    justify-content: start !important;
}

/* Flower "garden" animation (growing from the bottom edge) */
.garden-container {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 30px;
    pointer-events: none;
    overflow: hidden;
}

.flower {
    position: absolute;
    bottom: 0;
    width: 18px;
    transform: translateX(-50%);
    --stem-h: 18px;
    --grow-dur: 6s;
    --grow-delay: 0s;
    --bloom: #ffeb3b;
}

.flower .stem {
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 2px;
    height: var(--stem-h);
    transform: translateX(-50%) scaleY(0);
    transform-origin: bottom;
    border-radius: 2px;
    background: linear-gradient(#b9f6ca, #2e7d32);
    animation: stemGrow var(--grow-dur) ease-in-out infinite;
    animation-delay: var(--grow-delay);
}

.flower .bloom {
    position: absolute;
    left: 50%;
    bottom: calc(var(--stem-h) - 2px);
    width: 10px;
    height: 10px;
    transform: translateX(-50%) scale(0);
    transform-origin: center;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 35%, #ffffffcc, var(--bloom));
    box-shadow:
        0 -7px 0 -3px var(--bloom),
        7px 0 0 -3px var(--bloom),
        0 7px 0 -3px var(--bloom),
        -7px 0 0 -3px var(--bloom);
    animation: bloomPop var(--grow-dur) ease-in-out infinite;
    animation-delay: var(--grow-delay);
}

@keyframes stemGrow {
    0%,
    15% {
        transform: translateX(-50%) scaleY(0);
        opacity: 0;
    }
    35% {
        transform: translateX(-50%) scaleY(1);
        opacity: 1;
    }
    80% {
        transform: translateX(-50%) scaleY(1);
        opacity: 1;
    }
    100% {
        transform: translateX(-50%) scaleY(0);
        opacity: 0;
    }
}

@keyframes bloomPop {
    0%,
    35% {
        transform: translateX(-50%) scale(0);
        opacity: 0;
    }
    50% {
        transform: translateX(-50%) scale(1);
        opacity: 1;
    }
    80% {
        transform: translateX(-50%) scale(1);
        opacity: 1;
    }
    100% {
        transform: translateX(-50%) scale(0);
        opacity: 0;
    }
}

/*
Snowflake animation styles (winter theme) — kept for later use.
.snow-container {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.snowflake {
    position: absolute;
    top: -20px;
    color: rgba(255, 255, 255, 0.95);
    text-shadow: 0 0 3px rgba(0, 0, 0, 0.25);
    animation-name: snowFall;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
}

@keyframes snowFall {
    0% {
        transform: translateY(0) translateX(0);
        opacity: 0;
    }
    10% {
        opacity: 1;
    }
    100% {
        transform: translateY(80px) translateX(15px);
        opacity: 0;
    }
}
*/

.text-ellipsis {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
